// 1T fork: bold / italic for text elements (upstream Excalidraw has neither).
// Kept in its own file so upstream merges of actionProperties.tsx stay clean.
import clsx from "clsx";

import { KEYS, deriveStylesPanelMode } from "@excalidraw/common";

import {
  getBoundTextElement,
  isTextElement,
  newElementWith,
  redrawTextBoundingBox,
} from "@excalidraw/element";

import { CaptureUpdateAction } from "@excalidraw/element";

import type {
  ExcalidrawElement,
  ExcalidrawTextElement,
  FontStyle,
  FontWeight,
} from "@excalidraw/element/types";

import { createIcon } from "../components/icons";
import { withCaretPositionPreservation } from "../hooks/useTextEditorFocus";
import { t } from "../i18n";
import { getSelectedElements } from "../scene";
import { getShortcutKey } from "../shortcut";

import { changeProperty } from "./actionProperties";
import { register } from "./register";

import type { AppClassProperties, AppState } from "../types";

const iconProps = {
  width: 24,
  height: 24,
  fill: "none",
  strokeWidth: 2,
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

// tabler icons: bold, italic
export const TextBoldIcon = createIcon(
  <g>
    <path d="M7 5h6a3.5 3.5 0 0 1 0 7h-6z" />
    <path d="M13 12h1a3.5 3.5 0 0 1 0 7h-7v-7" />
  </g>,
  iconProps,
);

export const TextItalicIcon = createIcon(
  <g>
    <path d="M11 5l6 0" />
    <path d="M7 19l6 0" />
    <path d="M14 5l-4 14" />
  </g>,
  iconProps,
);

type TextStyleKey = "fontWeight" | "fontStyle";

const STYLE_ON = {
  fontWeight: "bold",
  fontStyle: "italic",
} as const;

const APP_STATE_KEY = {
  fontWeight: "currentItemFontWeight",
  fontStyle: "currentItemFontStyle",
} as const;

/** text elements the toggle applies to: selected texts and bound texts of selected containers */
const getTargetTextElements = (
  elements: readonly ExcalidrawElement[],
  appState: AppState,
  app: AppClassProperties,
): ExcalidrawTextElement[] => {
  if (appState.editingTextElement) {
    const editing = app.scene.getElement(appState.editingTextElement.id);
    return editing && isTextElement(editing) ? [editing] : [];
  }
  const elementsMap = app.scene.getNonDeletedElementsMap();
  const result: ExcalidrawTextElement[] = [];
  for (const element of getSelectedElements(elements, appState)) {
    if (isTextElement(element)) {
      result.push(element);
    } else {
      const bound = getBoundTextElement(element, elementsMap);
      if (bound) {
        result.push(bound);
      }
    }
  }
  return result;
};

/**
 * Whether the style is "on" for the current selection (all targeted texts have
 * it), or — with no text targeted — for new text.
 */
export const isTextStyleActive = (
  key: TextStyleKey,
  elements: readonly ExcalidrawElement[],
  appState: AppState,
  app: AppClassProperties,
) => {
  const targets = getTargetTextElements(elements, appState, app);
  if (!targets.length) {
    return appState[APP_STATE_KEY[key]] === STYLE_ON[key];
  }
  return targets.every((element) => element[key] === STYLE_ON[key]);
};

const toggleTextStyle = (
  key: TextStyleKey,
  elements: readonly ExcalidrawElement[],
  appState: AppState,
  app: AppClassProperties,
) => {
  const nextValue: FontWeight | FontStyle = isTextStyleActive(
    key,
    elements,
    appState,
    app,
  )
    ? "normal"
    : STYLE_ON[key];

  return {
    elements: changeProperty(
      elements,
      appState,
      (oldElement) => {
        if (isTextElement(oldElement)) {
          const newElement: ExcalidrawTextElement = newElementWith(oldElement, {
            [key]: nextValue,
          });
          // bold/italic glyphs are wider — refit the box (and container)
          redrawTextBoundingBox(
            newElement,
            app.scene.getContainerElement(oldElement),
            app.scene,
          );
          return newElement;
        }
        return oldElement;
      },
      true,
    ),
    appState: {
      ...appState,
      [APP_STATE_KEY[key]]: nextValue,
    },
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  };
};

/** Ctrl/Cmd+B / Ctrl/Cmd+I (by physical key, so it works on any layout) */
const isTextStyleShortcut = (
  code: "KeyB" | "KeyI",
  event: React.KeyboardEvent | KeyboardEvent,
  appState: AppState,
  elements: readonly ExcalidrawElement[],
  app: AppClassProperties,
) =>
  event[KEYS.CTRL_OR_CMD] &&
  !event.shiftKey &&
  !event.altKey &&
  event.code === code &&
  (appState.activeTool.type === "text" ||
    getTargetTextElements(elements, appState, app).length > 0);

export const actionToggleTextBold = register({
  name: "toggleTextBold",
  label: "labels.fontWeightBold",
  icon: TextBoldIcon,
  trackEvent: false,
  perform: (elements, appState, _, app) =>
    toggleTextStyle("fontWeight", elements, appState, app),
  keyTest: (event, appState, elements, app) =>
    isTextStyleShortcut("KeyB", event, appState, elements, app),
});

export const actionToggleTextItalic = register({
  name: "toggleTextItalic",
  label: "labels.italic",
  icon: TextItalicIcon,
  trackEvent: false,
  perform: (elements, appState, _, app) =>
    toggleTextStyle("fontStyle", elements, appState, app),
  keyTest: (event, appState, elements, app) =>
    isTextStyleShortcut("KeyI", event, appState, elements, app),
});

const TEXT_STYLE_BUTTONS = [
  {
    key: "fontWeight",
    label: "labels.fontWeightBold",
    shortcut: "CtrlOrCmd+B",
    icon: TextBoldIcon,
    testId: "text-bold",
  },
  {
    key: "fontStyle",
    label: "labels.italic",
    shortcut: "CtrlOrCmd+I",
    icon: TextItalicIcon,
    testId: "text-italic",
  },
] as const;

/** "Text style" panel: bold and italic toggles (independent of each other) */
export const actionChangeTextStyle = register<TextStyleKey>({
  name: "changeTextStyle",
  label: "labels.textStyle",
  trackEvent: false,
  perform: (elements, appState, value, app) =>
    toggleTextStyle(value ?? "fontWeight", elements, appState, app),
  PanelComponent: ({ elements, appState, updateData, app, data }) => {
    const isCompact = deriveStylesPanelMode(app.editorInterface) !== "full";

    return (
      <fieldset>
        <legend>{t("labels.textStyle")}</legend>
        <div className="buttonList">
          {TEXT_STYLE_BUTTONS.map(({ key, label, shortcut, icon, testId }) => {
            const active = isTextStyleActive(key, elements, appState, app);
            const title = `${t(label)} — ${getShortcutKey(shortcut)}`;
            return (
              // toggles, not a radio group: aria-pressed exposes the state
              // to screen readers and accessibility-tree based checks
              <button
                key={key}
                type="button"
                title={title}
                aria-label={title}
                aria-pressed={active}
                data-testid={testId}
                className={clsx({ active })}
                onClick={() =>
                  withCaretPositionPreservation(
                    () => updateData(key),
                    isCompact,
                    !!appState.editingTextElement,
                    data?.onPreventClose,
                  )
                }
              >
                {icon}
              </button>
            );
          })}
        </div>
      </fieldset>
    );
  },
});
